import React from "react";
import useExcelUpload, {
  generateRandomAmount,
} from "../../../utils/hooks/useExcelUpload";
import UploadExcelDialog from "../utils/UploadExcelEnhanced";

const getInstructions = () => [
  ["Col", "Comments"],
  [
    "A",
    "Asset category name. If the name matches an existing category, that category will be updated. Otherwise, a new category will be created.",
  ],
  ["B", "Cost opening balance. Numeric values only."],
  ["C", "Cost additions. Numeric values only."],
  ["D", "Cost disposal. Numeric values only."],
  [
    "E",
    "Accumulated depreciation opening balance. Optional. Leave blank if not applicable.",
  ],
  [
    "F",
    "Accumulated depreciation additions. Optional. Leave blank if not applicable.",
  ],
  [
    "G",
    "Accumulated depreciation disposal. Optional. Leave blank if not applicable.",
  ],
];

const toNumber = (value) => (value === "" || value == null ? 0 : Number(value));
const toRoundedAmount = (value) => Math.round(toNumber(value));
const getCategoryKey = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const FaSummaryExcelUpload = ({
  existingCategories = [],
  onExcelAddRows,
  open,
  handleCloseDialog,
}) => {
  const {
    selectedFile,
    uploading,
    processing,
    alertMessage,
    alertSeverity,
    errorFileUrl,
    rowCount,
    handleCloseAlert,
    handleDownloadTemplate,
    handleFileChange,
    handleResetFile,
    handleUpload,
    getTemplateHeadersString,
  } = useExcelUpload({
    fileName: "fa_summary_upload_template.xlsx",
    instructions: getInstructions(),
    sampleData: Array.from({ length: 3 }, (_, index) => ({
      "Asset Category": `Asset Category ${index + 1}`,
      "Cost Opening Balance": generateRandomAmount(),
      "Cost Additions": generateRandomAmount(),
      "Cost Disposal": 0,
      "Accumulated Depreciation Opening Balance": "",
      "Accumulated Depreciation Additions": "",
      "Accumulated Depreciation Disposal": "",
    })),
    validationSchema: [
      { type: "text", required: true },
      { type: "number", required: true },
      { type: "number", required: true },
      { type: "number", required: true },
      { type: "number" },
      { type: "number" },
      { type: "number" },
    ],
    onUpload: (excelData, rowCount) => {
      if (rowCount > 0) {
        const existingCategoryMap = new Map(
          existingCategories
            .filter((category) => getCategoryKey(category.assetCategory))
            .map((category) => [
              getCategoryKey(category.assetCategory),
              category,
            ])
        );

        const mappedRows = excelData.map((row) => {
          const uploadedCategory = String(row[0] || "").trim();
          const existingCategory = existingCategoryMap.get(
            getCategoryKey(uploadedCategory)
          );

          return {
            assetId: existingCategory?.assetId,
            assetCategory: existingCategory?.assetCategory || uploadedCategory,
            cost: {
              openingBalance: toRoundedAmount(row[1]),
              additions: toRoundedAmount(row[2]),
              disposal: toRoundedAmount(row[3]),
            },
            accumulatedDepreciation: {
              openingBalance: toRoundedAmount(row[4]),
              additions: toRoundedAmount(row[5]),
              disposal: toRoundedAmount(row[6]),
            },
          };
        });

        return onExcelAddRows(mappedRows);
      }
    },
  });

  return (
    <UploadExcelDialog
      open={open}
      selectedFile={selectedFile}
      uploading={uploading}
      processing={processing}
      alertMessage={alertMessage}
      alertSeverity={alertSeverity}
      errorFileUrl={errorFileUrl}
      validRows={rowCount}
      onClose={handleCloseDialog}
      onDownloadTemplate={handleDownloadTemplate}
      onFileChange={handleFileChange}
      onUpload={handleUpload}
      onCloseAlert={handleCloseAlert}
      onReset={handleResetFile}
      templateHeadersString={getTemplateHeadersString()}
      templateInstructions={getInstructions()}
    />
  );
};

export default FaSummaryExcelUpload;
